import { Progress, Tag } from "antd";
import { DocLink, type DocLinkType } from "../../components/DocLink";
import { ProjectLink } from "../../components/ProjectLink";
import { fmtMVR } from "../../helpers/format";
import type { ExportColumns } from "../../helpers/csv";

// A progress bar that does not lie about overrun: Progress caps its own fill at 100, so
// past that the bar goes to "exception" and the label carries the real number.
const progress = (pct: number) => (
  <Progress percent={Math.min(pct, 100)} format={() => `${pct.toFixed(0)}%`} status={pct > 100.5 ? "exception" : undefined} size="small" />
);

export type Row = Record<string, unknown>;

// One registry, one page. Every §24 report that is a straight tabular read of an endpoint
// is declared here rather than in its own near-identical file; the handful with real
// interaction (stock movement, project item status) keep their own pages.

interface ReportDef {
  title: string;
  desc: string;
  endpoint: string;
  /** The table's columns, and the CSV export's — one definition renders both. */
  columns: ExportColumns<Row>;
  /** Which filter controls this report accepts, if any. */
  filter?: "project";
  /** The endpoint answers per project only — don't call it until one is chosen. */
  requiresProject?: boolean;
  note?: string;
}

const date = (v: unknown) => (v ? new Date(String(v)).toLocaleDateString() : "—");
const money = (v: unknown) => fmtMVR(v == null ? null : Number(v));
const tag = (v: unknown) => <Tag>{String(v ?? "").replace(/_/g, " ")}</Tag>;
// Report rows carry the project as flat projectId/projectName columns.
const projectCol = { title: "Project", render: (_: unknown, r: Row) => <ProjectLink id={r.projectId as number} name={r.projectName as string} /> };
// A site is always shown under its project; report rows carry both as flat columns.
const site = (idKey: string, nameKey: string, siteKey: string) => (_: unknown, r: Row) => (
  <>
    <ProjectLink id={r[idKey] as number} name={r[nameKey] as string} /> / {String(r[siteKey] ?? "")}
  </>
);

// One registry, one page. Every §24 report that is a straight tabular read of an endpoint
// lives here rather than in its own near-identical file; the handful with real interaction
// (stock movement, project item status) keep their own pages.
export const REPORT_DEFS: Record<string, ReportDef> = {
  "bom-variation-history": {
    title: "BOM Variation History",
    desc: "Every approved and pending change to a project's approved quantities.",
    endpoint: "/reports/bom-variation-history",
    filter: "project",
    requiresProject: true,
    columns: [
      { title: "Doc No", dataIndex: "docNo" },
      { title: "Status", dataIndex: "status", render: tag },
      { title: "Code", dataIndex: "itemCode" },
      { title: "Item", dataIndex: "itemName" },
      { title: "Change", dataIndex: "deltaQty", render: (v: number) => (v > 0 ? `+${v}` : v) },
      { title: "Reason", dataIndex: "reason", ellipsis: true },
      { title: "Raised", dataIndex: "createdAt", render: date },
    ],
  },
  "mrq-status": {
    title: "MRQ Status & Outstanding",
    desc: "Requested vs allocated, dispatched and received for every request.",
    endpoint: "/reports/mrq-status",
    columns: [
      { title: "Doc No", dataIndex: "docNo" },
      { title: "Site", render: site("projectId", "projectName", "siteName") },
      { title: "Status", dataIndex: "status", render: tag },
      { title: "Requested", dataIndex: "requestedQty" },
      { title: "Allocated", dataIndex: "allocatedQty" },
      { title: "Dispatched", dataIndex: "dispatchedQty" },
      { title: "Received", dataIndex: "receivedQty" },
      {
        title: "Outstanding",
        render: (_, r) => Number(r.requestedQty ?? 0) - Number(r.receivedQty ?? 0),
      },
    ],
  },
  "allocation-status": {
    title: "Fulfillment Allocation Status",
    desc: "Where committed quantities are being sourced from, and how far along they are.",
    endpoint: "/reports/allocation-status",
    columns: [
      { title: "Source", dataIndex: "sourceType", render: tag },
      { title: "Status", dataIndex: "status", render: tag },
      { title: "Allocations", dataIndex: "count" },
      { title: "Total qty", dataIndex: "totalQty" },
    ],
  },
  "cw-stock-summary": {
    title: "Central Warehouse Stock",
    desc: "On hand, reserved, available and damaged at Central and its quarantine.",
    endpoint: "/reports/cw-stock-summary",
    note: "Damaged stock sits in a separate quarantine location, so it never counts toward Available. Physical on hand is Central + quarantine.",
    columns: [
      { title: "Location", dataIndex: "locationName" },
      { title: "Type", dataIndex: "locationType", render: tag },
      { title: "Code", dataIndex: "itemCode" },
      { title: "Item", dataIndex: "itemName" },
      { title: "On hand", dataIndex: "onHand" },
      { title: "Reserved", dataIndex: "reserved" },
      { title: "Available", render: (_, r) => Number(r.onHand ?? 0) - Number(r.reserved ?? 0) },
    ],
  },
  "site-stock": {
    title: "Site Stock",
    desc: "On hand, reserved and available at every site store.",
    endpoint: "/reports/site-stock",
    columns: [
      { title: "Location", dataIndex: "locationName" },
      { title: "Code", dataIndex: "itemCode" },
      { title: "Item", dataIndex: "itemName" },
      { title: "On hand", dataIndex: "onHand" },
      { title: "Reserved", dataIndex: "reserved" },
      { title: "Available", render: (_, r) => Number(r.onHand ?? 0) - Number(r.reserved ?? 0) },
    ],
  },
  "transfer-status": {
    title: "Transfer Order & Note Status",
    desc: "Site-to-site movement, order and dispatch note side by side.",
    endpoint: "/reports/transfer-status",
    columns: [
      { title: "Doc No", dataIndex: "docNo" },
      { title: "Type", dataIndex: "type", render: (v: string) => <Tag color={v === "TO" ? "blue" : "geekblue"}>{v}</Tag> },
      { title: "Status", dataIndex: "status", render: tag },
      { title: "From", render: site("fromProjectId", "fromProjectName", "fromSiteName") },
      { title: "To", render: site("toProjectId", "toProjectName", "toSiteName") },
      { title: "Raised", dataIndex: "createdAt", render: date },
    ],
  },
  "procurement-shortage": {
    title: "Procurement Shortage",
    desc: "Committed quantities still waiting on a supplier.",
    endpoint: "/reports/procurement-shortage",
    columns: [
      projectCol,
      { title: "Code", dataIndex: "itemCode" },
      { title: "Item", dataIndex: "itemName" },
      { title: "Source", dataIndex: "sourceType", render: tag },
      { title: "Allocated", dataIndex: "allocatedQty" },
      { title: "Fulfilled", dataIndex: "fulfilledQty" },
      { title: "Outstanding", dataIndex: "outstandingQty" },
    ],
  },
  "direct-delivery-status": {
    title: "Direct Delivery Status",
    desc: "Purchase orders delivering straight to a site, never touching Central.",
    endpoint: "/reports/direct-delivery-status",
    columns: [
      { title: "PO", dataIndex: "docNo" },
      { title: "Supplier", dataIndex: "supplierName" },
      { title: "Site", render: site("projectId", "projectName", "siteName") },
      { title: "Status", dataIndex: "status", render: tag },
      { title: "Value", dataIndex: "totalLaari", render: money },
    ],
  },
  "consumption-by-activity": {
    title: "Consumption by Activity",
    desc: "What was used, on which work activity.",
    endpoint: "/reports/consumption-by-activity",
    filter: "project",
    columns: [
      { title: "Activity", dataIndex: "activity" },
      { title: "Code", dataIndex: "itemCode" },
      { title: "Item", dataIndex: "itemName" },
      { title: "Qty", dataIndex: "totalQty" },
    ],
  },
  "activity-progress": {
    title: "Activity Progress",
    desc: "How far each piece of work has got: consumed against the BOM it was approved for.",
    endpoint: "/reports/activity-progress",
    filter: "project",
    requiresProject: true,
    note:
      "Every row includes its sub-activities, so a top-level figure covers the whole branch (and the branch's rows repeat beneath it). " +
      "Materials in different units cannot be added up, so progress is shown two ways: by value (each item weighted by its last purchase price — trustworthy only as far as 'Priced' covers 'Items') " +
      "and as the plain average of each item's consumed-vs-approved ratio. Neither is capped: over 100% means more was consumed than the BOM approved.",
    columns: [
      // Depth is padded rather than rendered as a tree so the CSV export carries the same
      // shape the table shows.
      {
        title: "Activity",
        render: (_: unknown, r: Row) => <span style={{ paddingLeft: (r.depth as number) * 16 }}>{String(r.name ?? "")}</span>,
        exportValue: (r: Row) => String(r.activityPath ?? ""),
      },
      { title: "Code", dataIndex: "code" },
      { title: "Items", dataIndex: "itemCount" },
      { title: "Priced", dataIndex: "pricedItemCount" },
      { title: "Approved value", dataIndex: "approvedValueLaari", render: money },
      { title: "Consumed value", dataIndex: "consumedValueLaari", render: money },
      {
        title: "Progress (by value)",
        render: (_: unknown, r: Row) => (r.valueProgressPct == null ? "—" : progress(Number(r.valueProgressPct))),
        exportValue: (r: Row) => (r.valueProgressPct == null ? "" : `${Number(r.valueProgressPct).toFixed(1)}%`),
      },
      {
        title: "Progress (item average)",
        render: (_: unknown, r: Row) => progress(Number(r.avgItemProgressPct ?? 0)),
        exportValue: (r: Row) => `${Number(r.avgItemProgressPct ?? 0).toFixed(1)}%`,
      },
    ],
  },
  "adjustments-summary": {
    title: "Damage, Wastage & Adjustments",
    desc: "Approved adjustments by type.",
    endpoint: "/reports/adjustments-summary",
    note: "Damage moves stock to quarantine and is recoverable; wastage and loss are posted to the loss sink.",
    columns: [
      { title: "Type", dataIndex: "type", render: tag },
      { title: "Documents", dataIndex: "count" },
      { title: "Total qty", dataIndex: "totalQty" },
    ],
  },
  "project-material-value": {
    title: "Project Material Value (LPP)",
    desc: "Approved BOM quantity valued at each item's last purchase price.",
    endpoint: "/reports/project-material-value",
    filter: "project",
    note: "Items with no purchase history yet contribute zero value — they still show, so the quantity needing a price stays visible.",
    columns: [
      projectCol,
      { title: "Code", dataIndex: "itemCode" },
      { title: "Item", dataIndex: "itemName" },
      { title: "Approved qty", dataIndex: "approvedQty" },
      { title: "Last purchase price", dataIndex: "lppLaari", render: money },
      { title: "Value", dataIndex: "valueLaari", render: money },
    ],
  },

  // -- The dashboard's exception drill-downs. Each of these three counts something no
  // register can express as a filter, so the tile links here instead. --
  "overdue-transit": {
    title: "Overdue Transit",
    desc: "Dispatches still sitting in transit more than three days after they left.",
    endpoint: "/reports/overdue-transit",
    note: "Stock leaves an in-transit location as its receipt is posted, so a row here is quantity nobody has confirmed arriving.",
    columns: [
      {
        title: "Document",
        render: (_: unknown, r: Row) => <DocLink docType={r.refType as DocLinkType} id={r.refId as number} />,
        exportValue: (r: Row) => `${r.refType} #${r.refId}`,
      },
      { title: "From", dataIndex: "fromLocation" },
      { title: "To", dataIndex: "toLocation" },
      { title: "Dispatched", dataIndex: "openedAt", render: date },
      { title: "Days in transit", dataIndex: "daysInTransit" },
      { title: "Items", dataIndex: "openItemCount" },
      { title: "Qty outstanding", dataIndex: "remainingQty" },
    ],
  },
  "negative-risk-allocations": {
    title: "Negative-risk Allocations",
    desc: "Items committed to requests beyond what Central can currently dispatch.",
    endpoint: "/reports/negative-risk-allocations",
    note: "No single reservation can oversell stock; this is the forward view across all of them, so a shortfall means procurement or reallocation is needed before every allocation can be dispatched.",
    columns: [
      { title: "Code", dataIndex: "itemCode" },
      { title: "Item", dataIndex: "itemName" },
      { title: "Available at Central", dataIndex: "availableQty" },
      { title: "Outstanding allocations", dataIndex: "outstandingQty" },
      { title: "Shortfall", dataIndex: "shortfallQty" },
    ],
  },
  "bom-exhaustion": {
    title: "BOM Exhaustion",
    desc: "Approved BOM lines with nothing left to request.",
    endpoint: "/reports/bom-exhaustion",
    note: "A site cannot raise another request against these lines without an approved variation.",
    columns: [
      projectCol,
      { title: "Code", dataIndex: "itemCode" },
      { title: "Item", dataIndex: "itemName" },
      { title: "Approved", dataIndex: "approvedQty" },
      { title: "Committed", dataIndex: "committedQty" },
      { title: "Balance", dataIndex: "balanceQty" },
    ],
  },
};

// ============================================================
// The catalogue: every report in §24, generic and bespoke alike
// ============================================================

export interface ReportEntry {
  key: string;
  title: string;
  desc: string;
  group: string;
  path: string;
}

// The five reports with real interaction keep their own pages, so they are not in
// REPORT_DEFS above — but they are still reports, and anything listing reports has to
// list them too. The hub and global search both read the combined catalogue below rather
// than each keeping half of it.
const BESPOKE_REPORTS: Omit<ReportEntry, "path">[] = [
  { key: "project-item-status", title: "Project Item Status", desc: "BOM balances per project.", group: "Project" },
  { key: "bom-vs-actual", title: "BOM vs Actual", desc: "Approved vs consumed by item.", group: "Project" },
  { key: "stock-movement", title: "Stock Movement", desc: "Complete stock ledger by location and date.", group: "Stock" },
  { key: "procurement-status", title: "Procurement Status", desc: "PR and PO status overview.", group: "Procurement" },
  { key: "low-stock", title: "Low Stock", desc: "Items below their central reorder level.", group: "Stock" },
];

const REPORT_GROUPS: Record<string, string> = {
  "bom-variation-history": "Project",
  "mrq-status": "Requests",
  "allocation-status": "Requests",
  "cw-stock-summary": "Stock",
  "site-stock": "Stock",
  "transfer-status": "Movement",
  "procurement-shortage": "Procurement",
  "direct-delivery-status": "Procurement",
  "consumption-by-activity": "Project",
  "activity-progress": "Project",
  "adjustments-summary": "Stock",
  "project-material-value": "Project",
  "overdue-transit": "Exceptions",
  "negative-risk-allocations": "Exceptions",
  "bom-exhaustion": "Exceptions",
};

export const REPORT_CATALOG: ReportEntry[] = [
  ...BESPOKE_REPORTS,
  ...Object.entries(REPORT_DEFS).map(([key, def]) => ({
    key,
    title: def.title,
    desc: def.desc,
    group: REPORT_GROUPS[key] ?? "Other",
  })),
].map((r) => ({ ...r, path: `/reports/${r.key}` }));

